import { format, isValid, parseISO } from 'date-fns';
export const formatDate = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/** `<input type="date">` only accepts (and only ever reports) this shape. */
const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formats a value as the `YYYY-MM-DD` string an `<input type="date">` expects,
 * or `''` when there is no usable date.
 *
 * Unlike `formatDate`, this is **idempotent in every timezone**, which matters
 * whenever the result can be fed back in — a value already in `YYYY-MM-DD`
 * shape is returned untouched instead of being re-parsed. `formatDate` re-parses
 * `YYYY-MM-DD` as UTC midnight, so west of UTC it renders the *previous* day on
 * every pass; used as a normaliser it never reaches a fixed point.
 *
 * @param {unknown} value date, timestamp or date-ish string
 * @returns {string} `YYYY-MM-DD`, or `''` when the value is empty or unparseable
 */
export const toDateInputValue = (value) => {
  if (value === null || value === undefined || value === '') return '';

  if (typeof value === 'string' && DATE_INPUT_PATTERN.test(value)) {
    // Parsed as local time so `0000-00-00`-style placeholders are rejected.
    return isNaN(new Date(`${value}T00:00:00`).getTime()) ? '' : value;
  }

  const d = new Date(/** @type {string | number | Date} */ (value));
  if (isNaN(d.getTime())) return '';

  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

export const getYear = (date) => new Date(date).getFullYear();

export const getYearAndMonth = (date) =>
  new Date(date).toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
  });

export const getFullDateAndTime = (date) => {
  const d = new Date(date);
  return `${formatDate(date)} ${d.toLocaleTimeString('sv-SE')}`;
};

export const validDate = (date) => !isNaN(date.getTime());

export function formatDateToYearMonthDay(date) {
  if (date === '0000-00-00 00:00:00') return '';
  if (!isValid(date)) return '';

  return format(parseISO(date), "yyyy-MM-dd'");
}

export function formatDateToYearMonthDayHourMin(date) {
  if (date === '0000-00-00 00:00:00') return '';
  if (!isValid(date)) return '';

  return format(parseISO(date), "yyyy-MM-dd HH:mm'");
}

export const parseDate = (date) =>
  validDate(new Date(date)) ? new Date(date) : new Date();

/**
 * HH:MM ↔ minutes-since-midnight.
 *
 * Deliberately string math rather than date-fns: these operate on a
 * wall-clock time-of-day with no calendar day attached, and routing
 * through `Date` would drag DST into it — a schedule built on the
 * spring-forward Sunday would silently gain or lose a step. Callers that
 * need a real instant carry the zone separately.
 *
 * @param {string} hhmm
 * @returns {number} NaN when unparseable
 */
export const hhmmToMinutes = (hhmm) => {
  if (typeof hhmm !== 'string') return NaN;
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
};

/**
 * @param {number} mins minutes since midnight
 * @returns {string} HH:MM, zero-padded
 */
export const minutesToHhmm = (mins) => {
  const h = String(Math.floor(mins / 60)).padStart(2, '0');
  const m = String(mins % 60).padStart(2, '0');
  return `${h}:${m}`;
};
